import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RefundService } from '../../../../../../domain/services/cashering/refund.service';
import { Refund, RefundRequest } from '../../../../../../domain/models/refund.model';
import { RefundDialogComponent, RefundDialogData } from './refund-dialog.component';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AccountModel } from '../../../../../../domain/models/account.model';
import { PaymentDto } from '../../../../../../domain/models/payment.model';
import { CasheringService } from '../../../../../../domain/services/cashering/cashering.service';

/**
 * Account-scoped RECEIPTS tab: lists the account's payments and opens the
 * existing receipt view for any of them.
 */
@Component({
  selector: 'app-account-receipts-tab',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './account-receipts-tab.component.html',
  styleUrl: './account-receipts-tab.component.css'
})
export class AccountReceiptsTabComponent implements OnInit {
  @Input() account!: AccountModel;

  payments: PaymentDto[] = [];
  displayedColumns = ['paymentDate', 'receiptNumber', 'totalPayment', 'status', 'actions'];
  isLoading = true;
  loadError: string | null = null;

  /** Refunds across all of this account's payments, newest first. */
  refunds: Refund[] = [];
  refundColumns = ['refundNumber', 'receipt', 'requestedAt', 'refundAmount', 'refundMethod', 'status', 'refundActions'];
  refundError: string | null = null;
  busyRefundId: number | null = null;

  constructor(private casheringService: CasheringService, private router: Router,
              private refundService: RefundService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.loadPayments();
  }

  loadPayments(): void {
    if (!this.account?.id) {
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.loadError = null;
    this.casheringService.getPaymentsByAccount(this.account.id).subscribe({
      next: (response: any) => {
        this.payments = Array.isArray(response) ? response : (response?.content ?? []);
        this.isLoading = false;
        this.loadRefunds();
      },
      error: (error: any) => {
        console.error('Error loading receipts:', error);
        this.loadError = 'Unable to load receipts.';
        this.isLoading = false;
      }
    });
  }

  openReceipt(payment: PaymentDto): void {
    this.router.navigate(['/app/cashering', this.account.id, 'receipt', payment.id]);
  }

  // ---------- Refunds ----------

  private loadRefunds(): void {
    const completed = this.payments.filter(p => p.status === 'COMPLETED' || p.status === ('REFUNDED' as any));
    if (!completed.length) { this.refunds = []; return; }
    forkJoin(completed.map(p => this.refundService.listForPayment(p.id).pipe(catchError(() => of([] as Refund[])))))
      .subscribe(lists => {
        this.refunds = lists.flat().sort((a, b) => (b.requestedAt ?? '').localeCompare(a.requestedAt ?? ''));
      });
  }

  receiptFor(r: Refund): string {
    return this.payments.find(p => p.id === r.paymentId)?.receiptNumber ?? String(r.paymentId);
  }

  /** Amount still refundable on a payment = paid minus every refund not rejected/cancelled. */
  refundable(p: PaymentDto): number {
    const used = this.refunds
      .filter(r => r.paymentId === p.id && r.status !== 'REJECTED' && r.status !== 'CANCELLED')
      .reduce((s, r) => s + (r.refundAmount ?? 0), 0);
    return Math.max(0, Math.round(((p.totalPayment ?? 0) - used) * 100) / 100);
  }

  canRefund(p: PaymentDto): boolean {
    return p.status === 'COMPLETED' && this.refundable(p) > 0;
  }

  requestRefund(p: PaymentDto): void {
    const data: RefundDialogData = { mode: 'request', receiptNumber: p.receiptNumber, paymentAmount: p.totalPayment, refundable: this.refundable(p) };
    this.dialog.open<RefundDialogComponent, RefundDialogData, RefundRequest | string | undefined>(RefundDialogComponent, { data })
      .afterClosed().subscribe(result => {
        if (!result || typeof result === 'string') return;
        this.refundError = null;
        this.refundService.request(p.id, result).subscribe({
          next: () => this.loadRefunds(),
          error: (e) => this.refundError = e?.error?.message || e?.error?.error || 'Could not request the refund.'
        });
      });
  }

  approve(r: Refund): void { this.act(r, this.refundService.approve(r.id)); }
  process(r: Refund): void { this.act(r, this.refundService.process(r.id)); }

  reject(r: Refund): void {
    const data: RefundDialogData = { mode: 'reject', receiptNumber: this.receiptFor(r), paymentAmount: r.originalPaymentAmount, refundable: 0 };
    this.dialog.open<RefundDialogComponent, RefundDialogData, RefundRequest | string | undefined>(RefundDialogComponent, { data })
      .afterClosed().subscribe(reason => {
        if (typeof reason !== 'string' || !reason) return;
        this.act(r, this.refundService.reject(r.id, reason));
      });
  }

  private act(r: Refund, call: { subscribe: Function }): void {
    this.refundError = null;
    this.busyRefundId = r.id;
    call.subscribe({
      next: () => { this.busyRefundId = null; this.loadRefunds(); this.loadPayments(); },
      error: (e: any) => { this.busyRefundId = null; this.refundError = e?.error?.message || e?.error?.error || 'Refund action failed.'; }
    });
  }
}
